import React, { useState, useCallback, useEffect } from 'react';
import { PlaysGridRowManager } from 'services/PlaysGridRowManager';
import { observer } from 'mobx-react-lite';
import { ReactComponent as DownIcon } from 'components/icons/down.svg';
import { ISession } from 'interfaces/session/ISession';
import { ServiceTypes } from 'constants/ServiceTypes';
import { ISessionVerificationProvider } from 'interfaces/providers/ISessionVerificationProvider';
import { notify } from 'services/NotificationService';
import { LogService } from 'services/LogService';
import { useInjection } from '../../../../../services/ioc/ioc.react';
import { PitcherView } from './PitcherView/PitcherView';
import { BatterView } from './BatterView/BatterView';
import { BoxScoreContent, BoxScoreHeader, BoxScoreWrapper, HeaderTitle, StyledSelect } from './styles/BoxScoreStyles';
import DiscrepanciesBar from '../DiscrepanciesBar/DiscrepanciesBar';

interface IProps {
  rowManager: PlaysGridRowManager;
  session: ISession;
}

const PITCHERS_KEY = '--PITCHERS';
const BATTERS_KEY = '--BATTERS';

const BoxScore: React.FC<IProps> = observer(({ rowManager, session }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAwayTeamSelected, setIsAwayTeamSelected] = useState(true);
  const [isPitcherViewSelected, setIsPitcherViewSelected] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const sessionVerificationService = useInjection<ISessionVerificationProvider>(ServiceTypes.SessionVerificationApiService);
  const logService = useInjection<LogService>(ServiceTypes.LogService);
  const [discrepancyCount, setDiscrepancyCount] = useState(0);

  useEffect(() => {
    if (isOpen && !isInitialized) {
      initialize();
      setDiscrepancyCount(rowManager.boxScoreManager.awayPitcherDiscrepancies.length);
    }
  }, [isOpen]);

  const initialize = async () => {
    try {
      setIsLoading(true);
      await Promise.all([fetchSecondSourceBoxScoreData(), fetchTrackmanBoxScoreData(false)]);
      if (
        rowManager.boxScoreManager.trackmanBoxScore.sessionId !== '' &&
        rowManager.boxScoreManager.secondSourceBoxScore.sessionId !== ''
      ) {
        setIsInitialized(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSecondSourceBoxScoreData = async () => {
    try {
      const data = await sessionVerificationService.getSecondSourceBoxScore(session.id);
      rowManager.boxScoreManager.updateBoxScore(false, data);
    } catch (err) {
      logService.logError(err);
    }
  };

  const fetchTrackmanBoxScoreData = async (displayLoadingIndicator = true) => {
    try {
      displayLoadingIndicator && setIsLoading(true);
      const data = await sessionVerificationService.getTrackmanBoxScore({
        plays: rowManager.getLiveRows().map((r) => r.toBoxScorePlay()),
      });
      rowManager.boxScoreManager.updateBoxScore(true, data);
    } catch (err: unknown) {
      logService.logError(err);
      if (err instanceof Error) {
        notify('Error', err.message, 'error');
      }
    } finally {
      displayLoadingIndicator && setIsLoading(false);
    }
  };

  const getSelectOptions = useCallback(() => {
    const pitchersSuffix = ' - Pitchers';
    const battersSuffix = ' - Batters';
    const awayTeamName = `${session.awayTeam.name} (away)`;
    const homeTeamName = `${session.homeTeam.name} (home)`;
    return [
      {
        label: awayTeamName + pitchersSuffix,
        value: session.awayTeam.id + PITCHERS_KEY,
      },
      {
        label: awayTeamName + battersSuffix,
        value: session.awayTeam.id + BATTERS_KEY,
      },
      {
        label: homeTeamName + pitchersSuffix,
        value: session.homeTeam.id + PITCHERS_KEY,
      },
      {
        label: homeTeamName + battersSuffix,
        value: session.homeTeam.id + BATTERS_KEY,
      },
    ];
  }, [session]);

  const onTeamSelectChange = useCallback(
    (val: string) => {
      const isPitcherView = val.includes(PITCHERS_KEY);
      const teamId = val.split('--')[0];
      setIsAwayTeamSelected(session.awayTeam.id === teamId);
      setIsPitcherViewSelected(isPitcherView);
      rowManager.boxScoreManager.clearSelectedPlayers();
    },
    [session]
  );

  useEffect(() => {
    setDiscrepancyCount(() => {
      if (isPitcherViewSelected) {
        if (isAwayTeamSelected) {
          return rowManager.boxScoreManager.awayPitcherDiscrepancies.length;
        }
        return rowManager.boxScoreManager.homePitcherDiscrepancies.length;
      }
      if (isAwayTeamSelected) {
        return rowManager.boxScoreManager.awayBatterDiscrepancies.length;
      }
      return rowManager.boxScoreManager.homeBatterDiscrepancies.length;
    });
  }, [isPitcherViewSelected, isAwayTeamSelected, isOpen]);

  return (
    <BoxScoreWrapper>
      <BoxScoreHeader>
        <HeaderTitle>Box Score comparison</HeaderTitle> <DownIcon className={isOpen ? 'open' : ''} onClick={() => setIsOpen(!isOpen)} />
      </BoxScoreHeader>
      {isOpen && (
        <BoxScoreContent>
          <StyledSelect
            selectOptions={[...getSelectOptions()]}
            value={(isAwayTeamSelected ? session.awayTeam.id : session.homeTeam.id) + (isPitcherViewSelected ? PITCHERS_KEY : BATTERS_KEY)}
            onChange={(val: any) => onTeamSelectChange(val.target.value)}
            className='select-search'
            width='100%'
            label='Select team'
            disableUnderline
          />

          <DiscrepanciesBar discrepancyCount={discrepancyCount} onButtonClick={() => initialize()} />

          {!isPitcherViewSelected && (
            <BatterView session={session} rowManager={rowManager} isLoading={isLoading} isAwayTeam={isAwayTeamSelected} />
          )}
          {isPitcherViewSelected && (
            <PitcherView session={session} rowManager={rowManager} isLoading={isLoading} isAwayTeam={isAwayTeamSelected} />
          )}
        </BoxScoreContent>
      )}
    </BoxScoreWrapper>
  );
});

export default BoxScore;
